<template>
    <div class="main-panel">
        <div class="page-container">

            <!-- Page header -->
            <div class="page-header">
                <h1 class="page-title">Select A Bonus Winner</h1>
                <p class="page-subtitle">Pool entries from one or more competitions and run a bonus prize draw.</p>
            </div>

            <!-- Step 1: Select competitions -->
            <div class="step-card">
                <div class="step-header">
                    <span class="step-badge">1</span>
                    <span class="step-label">Select competitions</span>
                    <span v-if="selectedCompetitionIds.length" class="step-summary">
                        {{ selectedCompetitionIds.length }} selected
                    </span>
                </div>
                <div class="step-body">
                    <div class="competition-checkbox-list">
                        <label v-for="competition in activeCompetitions"
                               :key="competition.competitionId"
                               class="competition-checkbox-item">
                            <input type="checkbox"
                                   :value="competition.competitionId"
                                   v-model="selectedCompetitionIds" />
                            {{ competition.competitionTitle }} (WSP{{ competition.competitionId }})
                        </label>
                        <p v-if="!activeCompetitions.length" class="empty-hint">No active competitions found.</p>
                    </div>
                </div>
            </div>

            <!-- Step 2: Draw settings -->
            <div class="content-card">
                <div class="content-card-header">
                    <span class="card-title">Draw Settings</span>
                </div>
                <div class="content-card-body">
                    <div class="input-row">
                        <div class="field-group">
                            <label class="field-label" for="prize">Prize</label>
                            <input id="prize"
                                   type="text"
                                   class="field-input"
                                   v-model="prize"
                                   placeholder="e.g. BonusTV" />
                        </div>
                        <div class="field-group">
                            <label class="field-label" for="cutoff">Entry Cut-off Date &amp; Time</label>
                            <input id="cutoff"
                                   type="datetime-local"
                                   class="field-input"
                                   v-model="cutOffDateTime" />
                        </div>
                        <button class="btn-primary"
                                @click="selectBonusWinner"
                                :disabled="isSubmitting">
                            <span v-if="!isSubmitting">Select Bonus Winner</span>
                            <LoaderDots v-if="isSubmitting" />
                        </button>
                    </div>
                    <p class="empty-hint">Letters and numbers only for the prize name. Only entries submitted on or before the cut-off will be included in the draw.</p>
                </div>
            </div>

            <!-- Step 3: Draw results -->
            <div v-if="loadingWinner || bonusWinnerDetailsList.length" class="results-card">
                <div class="results-header">
                    <span class="results-title">Draw Results</span>
                    <span v-if="bonusWinnerDetailsList.length" class="results-count">{{ bonusWinnerDetailsList.length }}</span>
                </div>

                <div v-if="loadingWinner" class="loading-row" style="padding: 0.85rem 1rem;">
                    <span class="loading-spinner"></span>
                    Running bonus draw...
                </div>

                <div v-else class="table-wrap">
                    <table class="data-table">
                        <thead>
                            <tr>
                                <th>Prize</th>
                                <th>Competition Id</th>
                                <th>Tickets Entered</th>
                                <th>Winning Ticket</th>
                                <th>Winning User Id</th>
                                <th class="col-action"></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(winner, index) in bonusWinnerDetailsList" :key="index">
                                <td>{{ winner.prize }}</td>
                                <td>{{ winner.competitionId }}</td>
                                <td>{{ winner.numberOfTicketsEntered }}</td>
                                <td class="ticket">{{ winner.winningTicket }}</td>
                                <td class="user-id">{{ winner.userId }}</td>
                                <td class="col-action">
                                    <a target="_blank" class="btn-view-user" :href="`/users/${winner.userId}`" title="See winner details">&#8594;</a>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from "vue";
import competitionService from "../services/competition-service";
import bonusWinnersService from "../services/bonus-winners-service";
import ActiveCompetitionsResponse from "../responses/ActiveCompetitionsResponse";
import BonusWinnerDetailsResponse from "../responses/BonusWinnerDetailsResponse";
import { showAlert } from "../utils/global";
import LoaderDots from "../components/LoadingButtonDots.vue";
import Swal from 'sweetalert2';

export default defineComponent({
    components: {
        LoaderDots,
    },
    setup() {
        const activeCompetitions = ref<ActiveCompetitionsResponse[]>([]);
        const selectedCompetitionIds = ref<number[]>([]);
        const bonusWinnerDetailsList = ref<BonusWinnerDetailsResponse[]>([]);
        const loadingWinner = ref(false);
        const isSubmitting = ref(false);
        const prize = ref<string>('');
        const cutOffDateTime = ref<string>('');

        const MAX_LENGTH = 50;
        const ALPHANUMERIC_REGEX = /^[a-zA-Z0-9]+$/;

        const loadActiveCompetitions = async () => {
            try {
                const response = await competitionService.getActiveCompetitions();
                activeCompetitions.value = response ?? [];
            } catch (error) {
                console.error("Error fetching active competitions:", error);
            }
        };

        onMounted(async () => {
            await loadActiveCompetitions();
        });

        const validate = (): boolean => {
            if (!selectedCompetitionIds.value.length) {
                showAlert("No competitions selected", "Please select at least one competition.", "error");
                return false;
            }

            if (!prize.value.trim()) {
                showAlert("No prize entered", "Please enter a prize before running the draw.", "error");
                return false;
            }

            if (!ALPHANUMERIC_REGEX.test(prize.value.trim())) {
                showAlert("Invalid characters", "Only letters and numbers are allowed in the prize name.", "error");
                return false;
            }

            if (prize.value.trim().length > MAX_LENGTH) {
                showAlert("Prize too long", `Prize must be ${MAX_LENGTH} characters or fewer.`, "error");
                return false;
            }

            if (!cutOffDateTime.value) {
                showAlert("No cut-off time set", "Please select an entry cut-off date and time.", "error");
                return false;
            }

            return true;
        };

        const confirm = async (): Promise<boolean> => {
            const cutOffFormatted = new Date(cutOffDateTime.value).toLocaleString();
            const competitionsList = selectedCompetitionIds.value.map(id => `WSP${id}`).join(', ');

            const result = await Swal.fire({
                icon: 'question',
                title: 'Confirm Bonus Draw',
                html: `
                    <p><strong>Competitions:</strong> ${competitionsList}</p>
                    <p><strong>Prize:</strong> ${prize.value.trim()}</p>
                    <p><strong>Cut-off:</strong> ${cutOffFormatted}</p>
                    <p style="margin-top: 1rem;">Continue?</p>
                `,
                showCancelButton: true,
                confirmButtonText: 'Yes, run draw',
                cancelButtonText: 'Cancel'
            });

            return result.isConfirmed;
        };

        const selectBonusWinner = async () => {
            if (!validate()) return;
            if (!await confirm()) return;

            loadingWinner.value = true;
            isSubmitting.value = true;

            try {
                const response = await bonusWinnersService.selectBonusWinner({
                    competitionIds: selectedCompetitionIds.value,
                    isTest: false,
                    prize: prize.value.trim(),
                    excludedTickets: [],
                    cutOffDateTime: new Date(cutOffDateTime.value).toISOString()
                });

                if (response.isSuccess) {
                    response.prize = prize.value.trim();
                    bonusWinnerDetailsList.value.push(response);
                } else {
                    showAlert(
                        "Failed to Select Bonus Winner",
                        `There was a problem selecting the bonus winner: ${response.message}`,
                        "error"
                    );
                }
            } catch (error: any) {
                const message =
                    error?.response?.data?.errors?.[0] ??
                    error?.response?.data?.message ??
                    error?.message ??
                    "An unexpected error occurred.";

                showAlert(
                    "Failed to Select Bonus Winner",
                    `An error occurred while selecting the bonus winner: ${message}`,
                    "error"
                );
            } finally {
                loadingWinner.value = false;
                isSubmitting.value = false;
            }
        };

        return {
            activeCompetitions,
            selectedCompetitionIds,
            selectBonusWinner,
            bonusWinnerDetailsList,
            loadingWinner,
            isSubmitting,
            prize,
            cutOffDateTime,
        };
    },
});
</script>

<style scoped>

    .page-container {
        padding: 2rem 2.5rem;
        max-width: 860px;
    }

    /* ── Page header ──────────────────────────────────────── */
    .page-header {
        margin-bottom: 2rem;
        padding-bottom: 1.25rem;
        border-bottom: 2px solid var(--clr-primary);
    }

    .page-title {
        font-size: 1.6rem;
        font-weight: 700;
        color: var(--clr-primary);
        margin-bottom: 0.3rem;
    }

    .page-subtitle {
        font-size: 0.9rem;
        color: #666;
    }

    /* ── Step cards ───────────────────────────────────────── */
    .step-card {
        background: #fff;
        border: 1px solid #e0e0e0;
        border-radius: 8px;
        margin-bottom: 1.25rem;
        overflow: hidden;
        box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
    }

    .step-header {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        padding: 0.75rem 1.25rem;
        background: #f8f9fa;
        border-bottom: 1px solid #e0e0e0;
    }

    .step-badge {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 24px;
        height: 24px;
        background: var(--clr-primary);
        color: #fff;
        border-radius: 50%;
        font-size: 0.78rem;
        font-weight: 700;
        flex-shrink: 0;
    }

    .step-label {
        font-size: 0.9rem;
        font-weight: 600;
        color: #333;
    }

    .step-summary {
        margin-left: 0.5rem;
        font-size: 0.88rem;
        font-weight: 600;
        color: #333;
        flex: 1;
    }

    .step-body {
        padding: 1rem 1.25rem;
    }

    /* ── Competition checkbox list ────────────────────────── */
    .competition-checkbox-list {
        max-height: 220px;
        overflow-y: auto;
        display: grid;
        gap: 0.5rem;
    }

    .competition-checkbox-item {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        font-size: 0.9rem;
        color: #333;
    }

    /* ── Content card ─────────────────────────────────────── */
    .content-card {
        background: #fff;
        border: 1px solid #e0e0e0;
        border-radius: 8px;
        overflow: hidden;
        box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
        margin-bottom: 1.25rem;
    }

    .content-card-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 0.75rem 1.25rem;
        background: var(--clr-primary);
    }

    .card-title {
        font-size: 0.95rem;
        font-weight: 600;
        color: #fff;
    }

    .content-card-body {
        padding: 1.25rem;
    }

    /* ── Input row ────────────────────────────────────────── */
    .input-row {
        display: flex;
        align-items: flex-end;
        gap: 1rem;
        flex-wrap: wrap;
        margin-bottom: 0.75rem;
    }

    .field-group {
        display: flex;
        flex-direction: column;
        gap: 0.35rem;
        flex: 1;
        min-width: 200px;
    }

    .field-label {
        font-size: 0.78rem;
        font-weight: 700;
        color: #555;
        text-transform: uppercase;
        letter-spacing: 0.05em;
    }

    .field-input {
        padding: 0.55rem 0.75rem;
        border: 1px solid #ccc;
        border-radius: 5px;
        font-size: 0.9rem;
        width: 100%;
        transition: border-color 0.15s, box-shadow 0.15s;
    }

    .field-input:focus {
        outline: none;
        border-color: var(--clr-primary);
        box-shadow: 0 0 0 3px rgba(0, 123, 160, 0.12);
    }

    .btn-primary {
        padding: 0.55rem 1.6rem;
        background: var(--clr-primary);
        color: #fff;
        border: none;
        border-radius: 5px;
        font-size: 0.9rem;
        font-weight: 600;
        cursor: pointer;
        transition: opacity 0.15s;
        white-space: nowrap;
        align-self: flex-end;
    }

    .btn-primary:disabled {
        opacity: 0.4;
        cursor: not-allowed;
    }

    .btn-primary:not(:disabled):hover {
        opacity: 0.85;
    }

    /* ── Loading ──────────────────────────────────────────── */
    .loading-row {
        display: flex;
        align-items: center;
        gap: 0.6rem;
        font-size: 0.88rem;
        color: #666;
    }

    .loading-spinner {
        display: inline-block;
        width: 14px;
        height: 14px;
        border: 2px solid #ccc;
        border-top-color: var(--clr-primary);
        border-radius: 50%;
        animation: spin 0.7s linear infinite;
    }

    @keyframes spin { to { transform: rotate(360deg); } }

    /* ── Results panel ────────────────────────────────────── */
    .results-card {
        margin-top: 1.5rem;
        border: 1px solid #e0e0e0;
        border-radius: 8px;
        overflow: hidden;
    }

    .results-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 0.65rem 1rem;
        background: #f0faf4;
        border-bottom: 1px solid #c8ecd6;
    }

    .results-title {
        font-size: 0.88rem;
        font-weight: 700;
        color: #2a7a4e;
        margin-right: 0.5rem;
    }

    .results-count {
        font-size: 0.78rem;
        color: #2a7a4e;
        background: #c8ecd6;
        padding: 0.15rem 0.55rem;
        border-radius: 10px;
        font-weight: 600;
    }

    /* ── Table ────────────────────────────────────────────── */
    .table-wrap {
        overflow: hidden;
    }

    .data-table {
        width: 100%;
        border-collapse: collapse;
        font-size: 0.88rem;
    }

    .data-table th {
        text-align: left;
        padding: 0.55rem 0.85rem;
        font-size: 0.75rem;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        color: #555;
        border-bottom: 2px solid #e0e0e0;
    }

    .data-table td {
        padding: 0.6rem 0.85rem;
        border-bottom: 1px solid #f0f0f0;
        color: #333;
    }

    .data-table tbody tr:last-child td {
        border-bottom: none;
    }

    .data-table tbody tr:hover td {
        background: #f9fbfc;
    }

    .col-action { width: 52px; text-align: center; }

    .user-id {
        font-weight: 600;
        color: #333;
    }

    .ticket {
        font-family: monospace;
        font-size: 0.85rem;
        color: #555;
    }

    .btn-view-user {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 26px;
        height: 26px;
        background: var(--clr-primary);
        color: #fff;
        border-radius: 4px;
        text-decoration: none;
        font-size: 0.85rem;
        font-weight: 700;
        transition: opacity 0.15s;
    }

    .btn-view-user:hover {
        opacity: 0.8;
    }

    /* ── Empty hint ───────────────────────────────────────── */
    .empty-hint {
        font-size: 0.88rem;
        color: #999;
        font-style: italic;
        margin: 0;
    }

</style>
